/*
 * Copyright 2025 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.core.network.coingecko.api

import de.jensklingenberg.ktorfit.http.GET
import de.jensklingenberg.ktorfit.http.Path
import de.jensklingenberg.ktorfit.http.Query
import kpt.core.base.network.annotation.ApiBinding
import kpt.core.network.coingecko.dto.CoinDetailDto
import kpt.core.network.coingecko.dto.CoinMarketDto

/** CoinGecko public API v3. Base URL: [BASE_URL]. */
@ApiBinding("coingecko")
interface CoinGeckoApi {

    /**
     * One page of the market list, ordered by market cap descending by default.
     *
     * @param vsCurrency quote currency every price is expressed in.
     * @param order CoinGecko's sort key.
     * @param perPage page size.
     * @param page 1-based page number.
     */
    @GET("api/v3/coins/markets")
    suspend fun getMarkets(
        @Query("vs_currency") vsCurrency: String = "usd",
        @Query("order") order: String = "market_cap_desc",
        @Query("per_page") perPage: Int,
        @Query("page") page: Int,
    ): List<CoinMarketDto>

    /**
     * Full detail for one coin.
     *
     * The four `false` defaults switch off payload sections the detail screen does not render — CoinGecko returns them
     * by default and they dominate the response size.
     *
     * @param id CoinGecko's coin id.
     */
    @GET("api/v3/coins/{id}")
    suspend fun getCoinDetail(
        @Path("id") id: String,
        @Query("localization") localization: Boolean = false,
        @Query("tickers") tickers: Boolean = false,
        @Query("community_data") communityData: Boolean = false,
        @Query("developer_data") developerData: Boolean = false,
    ): CoinDetailDto
}
